// /src/pages/TitleAgentAdminPage.js
//
// Title Agent settings. Administers the sites this Title Agent knows about:
// the detail that prints on a TR-9, and what each site is allowed to do with
// one.
//
// yard_details and title_agent_sites are two tables but one idea to an
// administrator, so they are edited together here rather than on separate
// screens.

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

const API_BASE = "/api";

const EMPTY_SITE = {
  location: "",
  name: "",
  street: "",
  city: "",
  state: "MI",
  zip: "",
  license_number: "",
  can_originate: true,
  can_receive: true,
  default_destination: "",
};

const str = (v) => (v === null || v === undefined ? "" : String(v));

// The API speaks the form's vocabulary (name/street/city); the sites listing
// speaks yard_details' (yard_name/yard_street/...). Convert once, here.
function toDraft(site) {
  return {
    location: str(site.location),
    name: str(site.yard_name),
    street: str(site.yard_street),
    city: str(site.yard_city),
    state: str(site.yard_state),
    zip: str(site.yard_zip),
    license_number: str(site.license_number),
    can_originate: !!site.can_originate,
    can_receive: !!site.can_receive,
    default_destination: str(site.default_destination),
  };
}

export default function TitleAgentAdminPage() {
  const { token } = useContext(AuthContext);

  const [sites, setSites] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [savingKey, setSavingKey] = useState("");

  const [adding, setAdding] = useState(false);
  const [newSite, setNewSite] = useState(EMPTY_SITE);

  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const applySites = useCallback((list) => {
    setSites(list);
    const next = {};
    list.forEach((s) => {
      next[s.location] = toDraft(s);
    });
    setDrafts(next);
  }, []);

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${API_BASE}/tr9/admin/sites`, { headers: authHeaders });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      applySites(body.sites || []);
    } catch (e) {
      setErr(e?.message || "Failed to load Title Agent settings.");
    } finally {
      setBusy(false);
    }
  }, [authHeaders, applySites]);

  useEffect(() => {
    load();
  }, [load]);

  const edit = (location, key, value) =>
    setDrafts((d) => ({ ...d, [location]: { ...d[location], [key]: value } }));

  const dirty = (location) => {
    const original = sites.find((s) => s.location === location);
    if (!original) return false;
    return JSON.stringify(toDraft(original)) !== JSON.stringify(drafts[location]);
  };

  const saveSite = async (location) => {
    setSavingKey(location);
    setErr("");
    try {
      const res = await fetch(
        `${API_BASE}/tr9/admin/sites/${encodeURIComponent(location)}`,
        { method: "PUT", headers: authHeaders, body: JSON.stringify(drafts[location]) }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setNotice(`Saved ${location}.`);
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to save the site.");
    } finally {
      setSavingKey("");
    }
  };

  const addSite = async () => {
    if (!newSite.location.trim()) return;
    setSavingKey("__new__");
    setErr("");
    try {
      const res = await fetch(`${API_BASE}/tr9/admin/sites`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(newSite),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setNotice(`Added ${body.site.location}.`);
      setNewSite(EMPTY_SITE);
      setAdding(false);
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to add the site.");
    } finally {
      setSavingKey("");
    }
  };

  // A site may only default to somewhere that can actually receive, and never
  // to itself -- the same rule the API enforces, mirrored so the dropdown never
  // offers a choice that would be silently dropped.
  const destinationsFor = (location) =>
    sites.filter((s) => s.can_receive && s.location !== location);

  const FIELDS = [
    ["name", "Dealer name", 3],
    ["street", "Street", 3],
    ["city", "City", 2],
    ["state", "State", 1],
    ["zip", "ZIP", 1],
    ["license_number", "Dealer license number", 2],
  ];

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card className="mb-3">
            <Card.Header className="d-flex flex-wrap gap-3 align-items-center">
              <Link to="/admin" className="btn btn-outline-secondary btn-sm">
                ← Admin
              </Link>
              <div className="fw-bold">Title Agent Settings</div>
              <small className="text-muted">
                Sites, and what each may do on a TR-9
              </small>
              <div className="ms-auto d-flex gap-2">
                <Link to="/tr9" className="btn btn-outline-primary btn-sm">
                  Open TR-9
                </Link>
                <Button
                  size="sm"
                  variant={adding ? "outline-secondary" : "primary"}
                  onClick={() => setAdding((v) => !v)}
                >
                  {adding ? "Cancel" : "Add site"}
                </Button>
              </div>
            </Card.Header>

            <Card.Body>
              {err && <Alert variant="danger">{err}</Alert>}
              {notice && (
                <Alert variant="success" dismissible onClose={() => setNotice("")}>
                  {notice}
                </Alert>
              )}

              <div className="small text-muted mb-3">
                <strong>Can raise TR-9</strong> — this site may create transfer
                listings for the vehicles standing in it.{" "}
                <strong>Can receive</strong> — vehicles may be conveyed to this
                site, so it is offered as a destination.{" "}
                <strong>Default destination</strong> — pre-selected on the TR-9
                screen; the user can still pick another.
              </div>

              {busy ? (
                <div className="d-flex justify-content-center p-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  {adding && (
                    <Card className="mb-3 border-primary">
                      <Card.Body className="py-3">
                        <Row className="g-2">
                          <Col md={2}>
                            <Form.Label className="small text-muted mb-1">
                              Location *
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              maxLength={20}
                              placeholder="e.g. Alma"
                              value={newSite.location}
                              onChange={(e) =>
                                setNewSite((s) => ({ ...s, location: e.target.value }))
                              }
                            />
                          </Col>
                          {FIELDS.map(([key, label, span]) => (
                            <Col md={span} key={key}>
                              <Form.Label className="small text-muted mb-1">
                                {label}
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={newSite[key]}
                                onChange={(e) =>
                                  setNewSite((s) => ({ ...s, [key]: e.target.value }))
                                }
                              />
                            </Col>
                          ))}
                        </Row>
                        <div className="d-flex flex-wrap gap-3 align-items-center mt-3">
                          <Form.Check
                            type="checkbox"
                            id="new-originate"
                            label="Can raise TR-9"
                            checked={newSite.can_originate}
                            onChange={(e) =>
                              setNewSite((s) => ({ ...s, can_originate: e.target.checked }))
                            }
                          />
                          <Form.Check
                            type="checkbox"
                            id="new-receive"
                            label="Can receive"
                            checked={newSite.can_receive}
                            onChange={(e) =>
                              setNewSite((s) => ({ ...s, can_receive: e.target.checked }))
                            }
                          />
                          <Form.Select
                            size="sm"
                            style={{ maxWidth: 220 }}
                            value={newSite.default_destination}
                            onChange={(e) =>
                              setNewSite((s) => ({
                                ...s,
                                default_destination: e.target.value,
                              }))
                            }
                          >
                            <option value="">No default destination</option>
                            {destinationsFor(newSite.location).map((s) => (
                              <option key={s.location} value={s.location}>
                                Defaults to {s.location}
                              </option>
                            ))}
                          </Form.Select>
                          <Button
                            size="sm"
                            className="ms-auto"
                            disabled={!newSite.location.trim() || savingKey === "__new__"}
                            onClick={addSite}
                          >
                            {savingKey === "__new__" ? "Adding…" : "Add site"}
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {sites.map((site) => {
                    const draft = drafts[site.location] || toDraft(site);
                    const changed = dirty(site.location);
                    return (
                      <Card key={site.location} className="mb-3">
                        <Card.Body className="py-3">
                          <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                            <span className="fw-bold">{site.location}</span>
                            {!site.can_originate && (
                              <Badge bg="secondary">cannot raise TR-9</Badge>
                            )}
                            {!site.can_receive && (
                              <Badge bg="secondary">cannot receive</Badge>
                            )}
                            {site.default_destination && (
                              <Badge bg="info" text="dark">
                                → {site.default_destination}
                              </Badge>
                            )}
                          </div>

                          <Row className="g-2">
                            {FIELDS.map(([key, label, span]) => (
                              <Col md={span} key={key}>
                                <Form.Label className="small text-muted mb-1">
                                  {label}
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  value={draft[key]}
                                  onChange={(e) =>
                                    edit(site.location, key, e.target.value)
                                  }
                                />
                              </Col>
                            ))}
                          </Row>

                          <div className="d-flex flex-wrap gap-3 align-items-center mt-3">
                            <Form.Check
                              type="checkbox"
                              id={`originate-${site.location}`}
                              label="Can raise TR-9"
                              checked={draft.can_originate}
                              onChange={(e) =>
                                edit(site.location, "can_originate", e.target.checked)
                              }
                            />
                            <Form.Check
                              type="checkbox"
                              id={`receive-${site.location}`}
                              label="Can receive"
                              checked={draft.can_receive}
                              onChange={(e) =>
                                edit(site.location, "can_receive", e.target.checked)
                              }
                            />
                            <Form.Select
                              size="sm"
                              style={{ maxWidth: 240 }}
                              value={draft.default_destination}
                              onChange={(e) =>
                                edit(site.location, "default_destination", e.target.value)
                              }
                            >
                              <option value="">No default destination</option>
                              {destinationsFor(site.location).map((s) => (
                                <option key={s.location} value={s.location}>
                                  Defaults to {s.location}
                                </option>
                              ))}
                            </Form.Select>
                            <Button
                              size="sm"
                              className="ms-auto"
                              disabled={!changed || savingKey === site.location}
                              onClick={() => saveSite(site.location)}
                            >
                              {savingKey === site.location ? "Saving…" : "Save"}
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
